import React, { useState, useEffect } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    MenuItem,
    Typography,
    IconButton,
    CircularProgress,
    Box,
    Chip,
    Alert,
    Divider,
} from "@mui/material";
import { Close, PlaylistAddCheck } from "@mui/icons-material";
import { useSelector } from "react-redux";
import { useSnackbar } from "notistack";

import api from "../../api/axiosInstance";
import { selectStatusOptions } from "../../redux/slices/statusDefinitionsSlice";

const parseTrackingNumbers = (raw) =>
    raw
        .split(/[\n,]+/)
        .map((s) => s.trim())
        .filter(Boolean);

const BulkTrackingStatusDialog = ({ open, onClose, onUpdated }) => {
    const { enqueueSnackbar } = useSnackbar();
    const itemStatusOptions = useSelector(selectStatusOptions("item"));
    const [rawInput, setRawInput] = useState("");
    const [itemStatus, setItemStatus] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [result, setResult] = useState(null);

    useEffect(() => {
        if (!itemStatus && itemStatusOptions.length) {
            setItemStatus(itemStatusOptions[itemStatusOptions.length - 1].value);
        }
    }, [itemStatus, itemStatusOptions]);

    const trackingNumbers = parseTrackingNumbers(rawInput);

    const handleClose = () => {
        setRawInput("");
        setResult(null);
        onClose();
    };

    const handleSubmit = async () => {
        if (trackingNumbers.length === 0) return;
        setSubmitting(true);
        setResult(null);
        try {
            const { data } = await api.post("/shipments/bulk-update-tracking-status", {
                trackingNumbers,
                itemStatus,
            });
            setResult(data.data);
            enqueueSnackbar(data.message, { variant: "success" });
            onUpdated?.();
        } catch (err) {
            enqueueSnackbar(err.response?.data?.message || "Failed to update items", {
                variant: "error",
            });
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
            <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <Typography fontWeight={700}>Bulk Update Tracking Status</Typography>
                <IconButton size="small" onClick={handleClose}>
                    <Close fontSize="small" />
                </IconButton>
            </DialogTitle>

            <DialogContent dividers>
                <Typography variant="body2" color="text.secondary" mb={2}>
                    Paste or type item tracking numbers — one per line, or separated by commas. Every matching item
                    across any shipment will be updated to the status you choose below.
                </Typography>

                <TextField
                    fullWidth
                    multiline
                    rows={6}
                    label="Tracking Numbers"
                    placeholder={"533361699924\n382371222000\n123456789012"}
                    value={rawInput}
                    onChange={(e) => setRawInput(e.target.value)}
                    sx={{ mb: 1.5, "& textarea": { fontFamily: "monospace", fontSize: 13 } }}
                />

                {trackingNumbers.length > 0 && (
                    <Typography variant="caption" color="text.secondary" display="block" mb={2}>
                        {trackingNumbers.length} tracking number{trackingNumbers.length !== 1 ? "s" : ""} entered
                    </Typography>
                )}

                <TextField
                    select
                    fullWidth
                    label="New Item Status"
                    value={itemStatus}
                    onChange={(e) => setItemStatus(e.target.value)}
                    sx={{ mb: 2 }}
                >
                    {itemStatusOptions.map((s) => (
                        <MenuItem key={s.value} value={s.value}>
                            {s.label}
                        </MenuItem>
                    ))}
                </TextField>

                {result && (
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                        <Divider />
                        <Alert severity="success" sx={{ borderRadius: 2 }}>
                            Updated {result.updatedCount} item(s)
                        </Alert>
                        {result.notFound?.length > 0 && (
                            <Alert severity="warning" sx={{ borderRadius: 2 }}>
                                <Typography variant="body2" fontWeight={600} mb={0.5}>
                                    {result.notFound.length} tracking number(s) not found:
                                </Typography>
                                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                                    {result.notFound.map((tn) => (
                                        <Chip key={tn} label={tn} size="small" sx={{ fontFamily: "monospace", fontSize: 11 }} />
                                    ))}
                                </Box>
                            </Alert>
                        )}
                    </Box>
                )}
            </DialogContent>

            <DialogActions sx={{ px: 3, py: 2 }}>
                <Button onClick={handleClose} variant="outlined">
                    {result ? "Close" : "Cancel"}
                </Button>
                <Button
                    onClick={handleSubmit}
                    variant="contained"
                    startIcon={<PlaylistAddCheck />}
                    disabled={trackingNumbers.length === 0 || submitting}
                >
                    {submitting ? <CircularProgress size={18} color="inherit" /> : `Update ${trackingNumbers.length || ""} Item(s)`}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default BulkTrackingStatusDialog;